import { useEffect, useRef } from 'react';
import { useNotifications } from '../context/NotificationContext';
import ApiService from '../services/api';
import DetectionAPI from '../services/detectionApi';

interface ProcessingJob {
  id: string;
  type: 'offer_processing' | 'detection' | 'analysis';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: number;
  startTime: Date;
  offerId?: string;
  offerTitle?: string;
}

export const useProcessingJobMonitor = () => {
  const { addNotification, addToast } = useNotifications();
  const monitoredJobs = useRef<Map<string, ProcessingJob>>(new Map());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const addJobToMonitor = (job: ProcessingJob) => {
    monitoredJobs.current.set(job.id, job);
    
    // Show initial processing notification
    addToast({
      title: 'بدأت المعالجة',
      message: `جاري معالجة ${job.offerTitle || 'العرض الجديد'}...`,
      type: 'info',
      duration: 3000
    });
  };

  const removeJobFromMonitor = (jobId: string) => {
    monitoredJobs.current.delete(jobId);
  };

  const checkJobStatus = async (jobId: string, job: ProcessingJob) => {
    try {
      let status;
      let results;
      
      // Check job status based on type
      switch (job.type) {
        case 'offer_processing':
          status = await ApiService.getProcessingStatus(jobId);
          if (status.status === 'completed') {
            results = await ApiService.getProcessingResults(jobId);
          }
          break;
        case 'detection':
          status = await DetectionAPI.getDetectionStats();
          break;
        case 'analysis':
          status = await DetectionAPI.getAnalysisStatus(jobId);
          if (status.status === 'completed') {
            results = await DetectionAPI.getAnalysisResults(jobId);
          }
          break;
        default:
          return;
      }

      const currentJob = monitoredJobs.current.get(jobId);
      if (!currentJob) return;

      // Update job status
      const updatedJob = { ...currentJob, status: status.status, progress: status.progress };
      monitoredJobs.current.set(jobId, updatedJob);

      // Handle completion
      if (status.status === 'completed' && currentJob.status !== 'completed') {
        handleJobCompletion(jobId, updatedJob, results);
      } else if (status.status === 'failed' && currentJob.status !== 'failed') {
        handleJobFailure(jobId, updatedJob, status.error);
      }

    } catch (error) {
      console.error(`Failed to check status for job ${jobId}:`, error);
      
      // Mark job as failed after 3 consecutive errors
      const currentJob = monitoredJobs.current.get(jobId);
      if (currentJob) {
        handleJobFailure(jobId, currentJob, 'فشل في الاتصال بالخادم');
      }
    }
  };

  const handleJobCompletion = (jobId: string, job: ProcessingJob, results?: any) => {
    const processingTime = Math.round((Date.now() - job.startTime.getTime()) / 1000);
    
    // Success toast notification
    addToast({
      title: 'تمت المعالجة بنجاح! 🎉',
      message: getCompletionMessage(job, results, processingTime),
      type: 'success',
      duration: 5000
    });

    // Detailed notification in sidebar
    addNotification({
      title: 'معالجة مكتملة',
      message: getDetailedCompletionMessage(job, results, processingTime),
      type: 'success',
      actions: results ? [{
        label: 'عرض النتائج',
        onClick: () => {
          // Navigate to results page
          if (job.type === 'offer_processing' && job.offerId) {
            window.location.href = `/offers/${job.offerId}`;
          }
        },
        variant: 'primary' as const
      }] : undefined
    });

    // Remove from monitoring
    removeJobFromMonitor(jobId);
  };

  const handleJobFailure = (jobId: string, job: ProcessingJob, error?: string) => {
    const processingTime = Math.round((Date.now() - job.startTime.getTime()) / 1000);
    
    // Error toast notification
    addToast({
      title: 'فشل في المعالجة ⚠️',
      message: `فشل في معالجة ${job.offerTitle || 'العرض'}: ${error || 'خطأ غير معروف'}`,
      type: 'error',
      duration: 7000
    });

    // Detailed notification in sidebar
    addNotification({
      title: 'فشل في المعالجة',
      message: `فشل في معالجة ${job.offerTitle || 'العرض'} بعد ${processingTime} ثانية. السبب: ${error || 'خطأ غير معروف'}`,
      type: 'error',
      actions: [{
        label: 'إعادة المحاولة',
        onClick: () => {
          // Retry logic could go here
          addToast({
            title: 'إعادة المحاولة',
            message: 'سيتم إعادة المعالجة قريباً',
            type: 'info'
          });
        },
        variant: 'secondary' as const
      }]
    });

    // Remove from monitoring
    removeJobFromMonitor(jobId);
  };

  const getCompletionMessage = (job: ProcessingJob, results: any, processingTime: number) => {
    switch (job.type) {
      case 'offer_processing':
        const productCount = results?.products_extracted || 0;
        return `تم استخراج ${productCount} منتج في ${processingTime} ثانية`;
      case 'detection':
        return `تم اكتشاف المنتجات بنجاح في ${processingTime} ثانية`;
      case 'analysis':
        return `تم تحليل النصوص بنجاح في ${processingTime} ثانية`;
      default:
        return `تمت المعالجة بنجاح في ${processingTime} ثانية`;
    }
  };

  const getDetailedCompletionMessage = (job: ProcessingJob, results: any, processingTime: number) => {
    switch (job.type) {
      case 'offer_processing':
        const productCount = results?.products_extracted || 0;
        const avgConfidence = results?.avg_confidence ? (results.avg_confidence * 100).toFixed(1) : 'غير محدد';
        return `تم إنتهاء معالجة ${job.offerTitle || 'العرض'} بنجاح. تم استخراج ${productCount} منتج بمتوسط ثقة ${avgConfidence}% في ${processingTime} ثانية.`;
      case 'detection':
        return `تم اكتشاف المنتجات في الصور بنجاح خلال ${processingTime} ثانية.`;
      case 'analysis':
        return `تم تحليل النصوص العربية وأستخراج البيانات بنجاح خلال ${processingTime} ثانية.`;
      default:
        return `تمت العملية بنجاح خلال ${processingTime} ثانية.`;
    }
  };

  // Start monitoring
  const startMonitoring = () => {
    if (intervalRef.current) return;

    intervalRef.current = setInterval(() => {
      monitoredJobs.current.forEach((job, jobId) => {
        if (job.status === 'pending' || job.status === 'processing') {
          checkJobStatus(jobId, job);
        }
      });
    }, 2000); // Check every 2 seconds
  };

  // Stop monitoring
  const stopMonitoring = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Auto-start monitoring when jobs are added
  useEffect(() => {
    if (monitoredJobs.current.size > 0 && !intervalRef.current) {
      startMonitoring();
    } else if (monitoredJobs.current.size === 0 && intervalRef.current) {
      stopMonitoring();
    }
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopMonitoring();
    };
  }, []);

  // Public API
  const monitorOfferProcessing = (jobId: string, offerId: string, offerTitle: string) => {
    addJobToMonitor({
      id: jobId,
      type: 'offer_processing',
      status: 'pending',
      startTime: new Date(),
      offerId,
      offerTitle
    });
  };

  const monitorDetection = (jobId: string) => {
    addJobToMonitor({
      id: jobId,
      type: 'detection',
      status: 'pending',
      startTime: new Date()
    });
  };

  const monitorAnalysis = (jobId: string) => {
    addJobToMonitor({
      id: jobId,
      type: 'analysis',
      status: 'pending',
      startTime: new Date()
    });
  };

  const getActiveJobs = () => {
    return Array.from(monitoredJobs.current.values()).filter(
      job => job.status === 'pending' || job.status === 'processing'
    );
  };

  return {
    monitorOfferProcessing,
    monitorDetection,
    monitorAnalysis,
    getActiveJobs,
    startMonitoring,
    stopMonitoring
  };
};